import { useStyletron } from '@thanx/uikit/theme'
import type {
  LiveUpdatesConfiguration,
  LiveUpdatesStack,
} from 'api/liveUpdates.types'
import React from 'react'
import { PAGE_MAX_WIDTH } from '../index.constants'
import ConnectionStatusBanner from './overview/ConnectionStatusBanner'
import HeroSection from './overview/HeroSection'
import HowItWorksSection from './overview/HowItWorksSection'
import {
  needsSetupAction,
  resolveProviderStatusContent,
  resolveProviderStatusKind,
} from './overview/providerStatus'
import ProviderStatusSection from './overview/ProviderStatusSection'
import SetupDisabledCard from './overview/SetupDisabledCard'
import WhyItMattersSection from './overview/WhyItMattersSection'
import type { TrackerStage } from './preview'
import type { OloWebhookPanel } from './trigger'

const STACK_GAP = '32px'

// The setup preview is the last thing on the tab, so the page needs more room
// beneath it than the gap between two sections — otherwise the card ends flush
// against the bottom of the scroll with nothing to breathe into.
const PAGE_BOTTOM_PADDING = '64px'

export interface OverviewTabProps {
  stack: LiveUpdatesStack | null
  deliveryConfigurable: boolean
  /** False for an unsupported (non-configurable) group — FR-76. The
   * education sections above still show (they're stack-agnostic); the setup
   * preview's picker stays too — only the rail/connection content below it
   * is replaced by a locked notice (Sandbox QA: merchants need a way back to
   * a working configuration). */
  isSupported: boolean
  isLiveUpdatesDisabled: boolean
  isOloOrdering: boolean
  isFlybuyProvider: boolean
  oloWebhook: OloWebhookPanel | undefined
  onManageOloWebhook: () => void
  onSetUp: () => void
  /** The merchant's uploaded logo — the hero's decorative Live Activity mock
   * uses this (and merchantName) instead of a generic placeholder, same as
   * the real per-configuration preview on the Configuration tab. */
  appIconUrl: string | undefined
  merchantName: string | undefined
  /** Tracker stages switched off for this configuration — the setup
   * preview's rail shows only what a guest would actually see. */
  disabledStages: Set<TrackerStage>
  /** Tracker stages driven by the estimated ready time rather than a provider
   * event — the setup preview's rail marks them "Estimated". */
  timerStages: Set<TrackerStage>
  configurations: LiveUpdatesConfiguration[]
  selectedId: string | null
  onSelectConfiguration: (selected: { value: string }[]) => void
  /** True while the SELECTED configuration's own detail (statuses) hasn't
   * caught up yet — briefly true after a switch, once the shell has already
   * rendered once (see index.tsx's `hasLoadedShellRef`). `disabledStages`/
   * `timerStages` default to empty during this gap, which reads as "nothing
   * is disabled" rather than "unknown yet" — the setup preview's rail must
   * not render from them while this is true, or it flashes every stage as
   * available even for a configuration that has some switched off. */
  isDetailLoading: boolean
  /** True once the SELECTED configuration's detail will never become
   * previewable as fetched — either a genuine fetch error, or a detail that
   * loaded but can't report a numeric version. Mutually exclusive with
   * `isDetailLoading`. The setup preview's rail is replaced by a retry
   * notice while this is true, same slot the loading placeholder uses,
   * rather than rendering from a detail that can never resolve into real
   * content. */
  isDetailUnavailable: boolean
  /** Retries the selected configuration's detail fetch — wired to the retry
   * notice shown while `isDetailUnavailable` is true. */
  onRetryDetail: () => void
  onTurnOn?: () => void
}

// The Overview tab (Sketch prototype live-updates-onboarding v29, FR-7/
// FR-36/FR-37): the landing IS the education (hero, why-it-matters, how-it-
// works are permanent, not a dismissible callout), followed by a real,
// per-configuration connection-status section.
const OverviewTab = ({
  stack,
  deliveryConfigurable,
  isSupported,
  isLiveUpdatesDisabled,
  isOloOrdering,
  isFlybuyProvider,
  oloWebhook,
  onManageOloWebhook,
  onSetUp,
  appIconUrl,
  merchantName,
  disabledStages,
  timerStages,
  configurations,
  selectedId,
  onSelectConfiguration,
  isDetailLoading,
  isDetailUnavailable,
  onRetryDetail,
  onTurnOn,
}: OverviewTabProps) => {
  const [css] = useStyletron()

  // Resolved once here, then handed to both surfaces that report it, so the
  // banner above the hero and the setup section can never disagree. Undefined
  // while Olo's connection queries are still settling — neither renders then,
  // rather than flashing a false state.
  const statusContent = resolveProviderStatusContent(
    resolveProviderStatusKind(isOloOrdering, isFlybuyProvider),
    oloWebhook
  )
  // The education layer is permanent, but the setup chrome is not: the banner
  // and the action block appear only while the merchant still has something
  // to do — Olo webhooks to connect, or a first event that hasn't arrived.
  const needsSetup = !!statusContent && needsSetupAction(statusContent)
  const hasPublished =
    !isLiveUpdatesDisabled &&
    configurations.some(configuration => !!configuration.lastPublishedAt)

  return (
    <div
      className={css({
        maxWidth: PAGE_MAX_WIDTH,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: STACK_GAP,
        padding: `32px 24px ${PAGE_BOTTOM_PADDING}`,
      })}
    >
      {stack &&
        isSupported &&
        statusContent &&
        needsSetup &&
        !isLiveUpdatesDisabled && (
          <ConnectionStatusBanner
            stack={stack}
            content={statusContent}
            onManageOloWebhook={onManageOloWebhook}
          />
        )}
      <HeroSection
        hasPublished={hasPublished}
        appIconUrl={appIconUrl}
        merchantName={merchantName}
      />
      <WhyItMattersSection
        appIconUrl={appIconUrl}
        merchantName={merchantName}
      />
      <HowItWorksSection appIconUrl={appIconUrl} />
      {/* The setup preview (picker + either the rail or the FR-76 locked
          notice) renders whenever a configuration is selected — including an
          unsupported one, so the picker stays put and a merchant who lands on
          it can switch back out (Sandbox QA). Only while an Olo connection's
          queries are still settling does it withhold rendering entirely,
          rather than flash a false "automatic" state. */}
      {isLiveUpdatesDisabled && <SetupDisabledCard onTurnOn={onTurnOn} />}
      {!isLiveUpdatesDisabled && stack && (!isSupported || statusContent) && (
        <ProviderStatusSection
          stack={stack}
          deliveryConfigurable={deliveryConfigurable}
          isSupported={isSupported}
          content={statusContent}
          onManageOloWebhook={onManageOloWebhook}
          disabledStages={disabledStages}
          timerStages={timerStages}
          configurations={configurations}
          selectedId={selectedId}
          onSelectConfiguration={onSelectConfiguration}
          onConfigure={onSetUp}
          isDetailLoading={isDetailLoading}
          isDetailUnavailable={isDetailUnavailable}
          onRetryDetail={onRetryDetail}
        />
      )}
    </div>
  )
}

export default OverviewTab
